// The event feed does not exist on the backend yet, so the dashboard ships the brief's own
// numbers the way DailyMissionsModal ships DAILY_MISSIONS_PLACEHOLDER. Swap this for the real
// feed when there is one - the components only read the types below.

import type { TFunction } from 'i18next';

import {
	RewardCardPurplePng,
	RewardCardSpadePng,
	RewardChestPng,
	RewardChipPng,
	RewardChipsRedPng,
	RewardCoinsPng,
	RewardGiftPng,
	RewardTrophyGoldPng,
	RewardTrophyPng,
	TrackFreePng,
	TrackPremiumPng,
} from '../../../assets/event';

export type EventReward = {
	id: string;
	/** Artwork exported from the brief; drawn in the tile's 36x40 icon slot. */
	icon: any;
	/** The line under the art: "x1", "50K", "250K". */
	amountLabel: string;
};

export type EventTrack = {
	id: string;
	title: string;
	/** Badge beside the track title. The brief's own rosette/crown art is not exported yet. */
	icon: any;
	/** Point totals under the rail, one per reward. */
	milestones: number[];
	rewards: EventReward[];
	/** Index of the milestone the player is standing on - everything before it is banked. */
	currentIndex: number;
	/** True when the reward at `currentIndex` is waiting to be collected. */
	claimable: boolean;
	/** Design sizes of one reward tile, in the 344-wide panel column. */
	tileWidth: number;
	tileHeight: number;
	tileGap: number;
	/** Premium track is greyed out until the pass is bought. */
	locked?: boolean;
};

export type EventData = {
	kicker: string;
	title: string;
	description: string;
	/** ms epoch the event closes at - the hero counts down to it. */
	endsAt: number;
	points: number;
	pointsGoal: number;
	freeTrack: EventTrack;
	premiumTrack: EventTrack;
	passPrice: string;
	passBenefits: string[];
};

const DAY = 24 * 60 * 60 * 1000;
const HOUR = 60 * 60 * 1000;
const MINUTE = 60 * 1000;

/** The brief shows "10d 14h 32m" left; without a real feed the clock starts from now. */
export const PLACEHOLDER_EVENT_DURATION = 10 * DAY + 14 * HOUR + 32 * MINUTE;

/** The UI copy goes through `t`; the event's own kicker/title stay mock data until the feed exists. */
export const createPlaceholderEvent = (t: TFunction, now: number = Date.now()): EventData => ({
	kicker: 'WORLD CUP 2026',
	title: 'SVARA CHALLENGE',
	description: t('T_SITE_EVENT_DESCRIPTION', {
		defaultValue: 'Play, win hands and collect Event Points\nto earn amazing rewards!',
	}),
	endsAt: now + PLACEHOLDER_EVENT_DURATION,
	points: 420,
	pointsGoal: 1200,
	freeTrack: {
		id: 'free',
		title: t('T_SITE_EVENT_FREE_TRACK', { defaultValue: 'FREE TRACK' }),
		icon: TrackFreePng,
		milestones: [0, 200, 600, 1200, 2000, 3000],
		rewards: [
			{ id: 'free-1', icon: RewardCardSpadePng, amountLabel: '×1' },
			{ id: 'free-2', icon: RewardCoinsPng, amountLabel: '50K' },
			{ id: 'free-3', icon: RewardCardPurplePng, amountLabel: '×2' },
			{ id: 'free-4', icon: RewardChipPng, amountLabel: '100K' },
			{ id: 'free-5', icon: RewardGiftPng, amountLabel: '×1' },
			{ id: 'free-6', icon: RewardTrophyPng, amountLabel: '×1' },
		],
		currentIndex: 1,
		claimable: true,
		tileWidth: 54,
		tileHeight: 79,
		tileGap: 4,
	},
	premiumTrack: {
		id: 'premium',
		title: t('T_SITE_EVENT_PREMIUM_TRACK', { defaultValue: 'PREMIUM TRACK' }),
		icon: TrackPremiumPng,
		milestones: [0, 200, 400, 800, 1600, 2400, 3500],
		rewards: [
			{ id: 'premium-1', icon: RewardTrophyPng, amountLabel: '×1' },
			{ id: 'premium-2', icon: RewardCardPurplePng, amountLabel: '×3' },
			{ id: 'premium-3', icon: RewardTrophyGoldPng, amountLabel: '×1' },
			{ id: 'premium-4', icon: RewardChestPng, amountLabel: '×1' },
			{ id: 'premium-5', icon: RewardCardSpadePng, amountLabel: '×1' },
			{ id: 'premium-6', icon: RewardChipsRedPng, amountLabel: '250K' },
			{ id: 'premium-7', icon: RewardChestPng, amountLabel: '×1' },
		],
		currentIndex: 3,
		claimable: true,
		tileWidth: 44,
		tileHeight: 75,
		tileGap: 6,
	},
	passPrice: '9,99 €',
	passBenefits: [
		t('T_SITE_EVENT_PREMIUM_REWARDS', { defaultValue: 'Premium rewards' }),
		t('T_SITE_EVENT_EXCLUSIVE_BADGE', { defaultValue: 'Exclusive badge' }),
		t('T_SITE_EVENT_MORE_REWARDS', { defaultValue: 'More rewards than Free Track' }),
	],
});

/** "10d 14h 32m", the hero's format; drops the day part once the event is under a day out. */
export const formatCountdown = (msLeft: number) => {
	const total = Math.max(0, Math.floor(msLeft / 1000));
	const days = Math.floor(total / 86400);
	const hours = Math.floor((total % 86400) / 3600);
	const minutes = Math.floor((total % 3600) / 60);
	const seconds = total % 60;

	if (days > 0) return `${days}d ${hours}h ${minutes}m`;
	if (hours > 0) return `${hours}h ${minutes}m`;
	return `${minutes}m ${seconds}s`;
};
